import { createSlice } from "@reduxjs/toolkit";
import { ReportDTO, ReportParameterDTO, ReportFavoriteDTO, FavoriteParametersDTO } from "../../models/Report.model";
import { GetReportDataResponse } from "../../models/ReportData.model";
import { GetReportParameters, GetReports, GetReportData, SaveFavorite, GetFavorites, GetFavoriteParameters, DeleteFavorite } from "../thunks/reportThunks";

export interface ReportState {
  reports: ReportDTO[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
  parameters: ReportParameterDTO | null;
  reportData: GetReportDataResponse | null;
  reportLoading: boolean;
  favorites: ReportFavoriteDTO[];
  favoritesLoading: boolean;
  currentFavorite: FavoriteParametersDTO | null;
  favoriteLoading: boolean;
  errorDeleteFavorite: string | null;
  saveFavoriteLoading: boolean;
}

const initialState: ReportState = {
  reports: [],
  parameters: null,
  reportData: null,
  loading: false,
  reportLoading: false,
  error: null,
  initialized: false,
  favorites: [],
  favoritesLoading: false,
  currentFavorite: null,
  favoriteLoading: false,
  saveFavoriteLoading: false,
  errorDeleteFavorite: null,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReports: (state) => {
      state.reports = [];
      state.initialized = false;
      state.error = null;
    },
    clearReportParameters: (state) => {
      state.parameters = null;
      state.initialized = false;
      state.error = null;
    },
    clearReportData: (state) => {
      state.reportData = null;
    },
    clearFavorites: (state) => {
      state.favorites = [];
      state.currentFavorite = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.reports;
        state.initialized = true;
      })
      .addCase(GetReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch reports";
      })
      .addCase(GetReportParameters.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(GetReportParameters.fulfilled, (state, action) => {
        state.loading = false;
        state.parameters = action.payload.parameters;
        state.initialized = true;
      })
      .addCase(GetReportParameters.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error.message || "Failed to load parameters";
      })
      .addCase(GetReportData.pending, (state) => {
        state.reportLoading = true;
        state.error = null;
      })
      .addCase(GetReportData.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reportData = action.payload;
      })
      .addCase(GetReportData.rejected, (state, action) => {
        state.reportLoading = false;
        state.error = action.payload || "Failed to load report data";
      })
      .addCase(SaveFavorite.pending, (state) => {
        state.saveFavoriteLoading = true;
        state.error = null;
      })
      .addCase(SaveFavorite.fulfilled, (state) => {
        state.saveFavoriteLoading = false;
      })
      .addCase(SaveFavorite.rejected, (state, action) => {
        state.saveFavoriteLoading = false;
        state.error = action.payload || "Failed to save favorite";
      })
      .addCase(GetFavorites.pending, (state) => {
        state.favoritesLoading = true;
        state.error = null;
      })
      .addCase(GetFavorites.fulfilled, (state, action) => {
        state.favoritesLoading = false;
        state.favorites = action.payload.favorites;
      })
      .addCase(GetFavorites.rejected, (state, action) => {
        state.favoritesLoading = false;
        state.error = action.payload || "Failed to load favorites";
      })
      .addCase(GetFavoriteParameters.pending, (state) => {
        state.favoriteLoading = true;
        state.error = null;
      })
      .addCase(GetFavoriteParameters.fulfilled, (state, action) => {
        state.favoriteLoading = false;
        state.currentFavorite = action.payload.favoriteParameters;
      })
      .addCase(GetFavoriteParameters.rejected, (state, action) => {
        state.favoriteLoading = false;
        state.error = action.payload || "Failed to load favorite parameters";
      })
      .addCase(DeleteFavorite.pending, (state) => {
        state.favoriteLoading = true;
        state.error = null;
      })
      .addCase(DeleteFavorite.fulfilled, (state) => {
        state.favorites = state.favorites.filter(
          (fav) => fav.reportFavorite_ID !== state.currentFavorite?.favorite_ID
        );
        state.currentFavorite = null;
        state.favoriteLoading = false;
      })
      .addCase(DeleteFavorite.rejected, (state, action) => {
        state.errorDeleteFavorite = "לא ניתן למחוק מועדף זה.";
        state.favoriteLoading = false;
        state.error = action.payload || "Failed to delete favorite";
      });
      ;
  },
});

export const { clearError, clearReports, clearReportParameters, clearReportData, clearFavorites } = reportSlice.actions;
export default reportSlice.reducer;
